import styled from '@emotion/styled';
import { useTranslation } from '@hotjar/translations';
import { Text, tokens } from '@hotjar/hotsauce';

const MARKER_HEIGHT = 34;
const MARKER_WIDTH = 168;
const MARKER_MARGIN = 4;

const EngagementMarkerContainer = styled.div<{ top: number; left: number; right: number }>`
  position: absolute;
  ${({ top, left, right }) => `
    top: ${top}px;
    left: 50%;
    transform: translateX(-50%) translateX(${left}px) translateX(${right}px);
  `}
  background: ${tokens.color.white};
  border: 1px solid ${tokens.color.ui_border};
  ${tokens.elevation(2)};
  border-radius: 99px;
  padding: ${tokens.spacer.x1};
  display: flex;
  align-items: center;
  pointer-events: none;
  z-index: 999;
`;

export const Indicator = styled.div<{ color: string }>`
  border-radius: 99px;
  width: 8px;
  height: 8px;
  margin-right: ${tokens.spacer.x1};
  ${({ color }) => `
    background-color: ${color};
  `}
`;

const Message = styled(Text)`
  white-space: nowrap;
`;

const settingsPerScore = {
  1: { text: 'smart-panel.engagement-zones.level-1', color: 'rgba(255, 216,204, 0.5)' },
  2: { text: 'smart-panel.engagement-zones.level-2', color: 'rgba(255, 60, 0, 0.5)' },
  3: { text: 'smart-panel.engagement-zones.level-3', color: 'rgba(179, 42, 0, 0.7)' },
};

const getLabelTop = (zoneTop: number, zoneSize: number) => {
  let labelTop = -(MARKER_HEIGHT + MARKER_MARGIN);
  if (zoneTop - MARKER_HEIGHT < 0) {
    labelTop = zoneSize;
  }
  return labelTop;
};

const getLabelLeft = (zoneLeft: number, zoneSize: number) => {
  const overflow = zoneLeft - MARKER_WIDTH / 2;
  return overflow < 0 ? Math.abs(overflow) - zoneSize / 2 : 0;
};

const getLabelRight = (zoneLeft: number, zoneSize: number, gridWidth: number) => {
  const overflow = gridWidth - (zoneLeft + MARKER_WIDTH / 2 + zoneSize / 2);

  return overflow < 0 ? overflow : 0;
};

export const EngagementMarker = ({
  score,
  gridWidth,
  zonePosition,
}: {
  score: HeatmapAPI.EngagementZone['score'];
  gridWidth: number;
  zonePosition: {
    top: number;
    left: number;
    size: number;
  };
}): JSX.Element => {
  const { t } = useTranslation('heatmaps');

  const settings = settingsPerScore[score];

  return (
    <EngagementMarkerContainer
      top={getLabelTop(zonePosition.top, zonePosition.size)}
      left={getLabelLeft(zonePosition.left, zonePosition.size)}
      right={getLabelRight(zonePosition.left, zonePosition.size, gridWidth)}
    >
      <Indicator color={settings.color} />
      <Message as="span" textColor="text_default" textType="caption">
        {t(settings.text)}
      </Message>
    </EngagementMarkerContainer>
  );
};
